import { Mode } from './mode';
import { IClient } from './IClient';
import { ClientPause, isPauseActive } from './pause';
import { TrialGoal, activeTrialGoal } from './trialGoal';
import { Screen, ScreenStatus } from '../screen';

/** Nom d'un client sans nom en base (aussi écarté du titre d'onglet, useVenueTitle). */
export const DEFAULT_CLIENT_NAME = 'No Name';

export class Client implements IClient {
    /* The client identifier */
    _id: string = '';

    /* The client mode */
    _mode: Mode = Mode.Anim;

    /* The client language */
    _lang: string = 'en';

    /* The client name */
    _name: string = DEFAULT_CLIENT_NAME;

    /* The client is disabled */
    _disabled: boolean = false;

    /* Free-play mode enabled (airport mode where players choose / vote on games) */
    _freePlayEnabled: boolean = false;

    /* The list of game ids */
    _games: string[] = [];

    /* The list of instance ids */
    _instanceIDs: string[] = [];

    /* The list of realtime screens */
    _screens: { [id: string]: Screen } = {};

    /* Socket server token ('v2' = Node.js server) — see SOCKET_SERVERS */
    _socketServer?: string;

    /* The stripe customer id */
    _stripeCustomerId?: string;

    /* The stripe subscription id */
    _stripeSubscriptionId?: string;

    /* Onboarding tutorial — tri-state (cf IClient) */
    _onboardingCompleted?: boolean;

    /* Pause d'abonnement (écrite par stripe-b2b) — cf types/client/pause.ts */
    _pause?: ClientPause;

    /* Objectif « 3 soirées » de l'essai (écrit par unboared-b2b) — cf types/client/trialGoal.ts */
    _trialGoal?: TrialGoal;

    /* Formule (écrite par stripe-b2b seul) : 'pro' = « Ma marque » incluse ; absent = standard */
    _plan?: 'pro';

    get id(): string {
        return this._id;
    }

    get mode(): Mode {
        return this._mode;
    }

    get language(): string {
        return this._lang;
    }

    get name(): string {
        return this._name;
    }

    get disabled(): boolean {
        return this._disabled;
    }

    get freePlayEnabled(): boolean {
        return this._freePlayEnabled;
    }

    get instanceIDs(): string[] {
        return this._instanceIDs;
    }

    get gameIDs(): string[] {
        return this._games;
    }

    get socketServer(): string | undefined {
        return this._socketServer;
    }

    get stripeCustomerId(): string | undefined {
        return this._stripeCustomerId;
    }

    get stripeSubscriptionId(): string | undefined {
        return this._stripeSubscriptionId;
    }

    get onboardingCompleted(): boolean | undefined {
        return this._onboardingCompleted;
    }

    get pause(): ClientPause | undefined {
        return this._pause;
    }

    /** Objectif de l'essai à afficher maintenant ; null = rien à afficher
     *  (champ absent, illisible, ou essai terminé). Jamais sur la TV. */
    get plan(): 'pro' | undefined {
        return this._plan;
    }

    get trialGoal(): TrialGoal | null {
        return activeTrialGoal(this._trialGoal);
    }

    /** Accès aux jeux coupé : pause en cours et maintenant ∈ [startsAt, resumesAt). */
    get isPaused(): boolean {
        return isPauseActive(this._pause);
    }

    getScreen(screenID: string): Screen | undefined {
        return this._screens[screenID];
    }

    getScreenStatus(screenID: string): ScreenStatus {
        return this._screens[screenID].status;
    }

    getScreenName(screenID: string): string {
        return this._screens[screenID].name;
    }

    getScreenDescription(screenID: string): string {
        return this._screens[screenID].description;
    }

    getScreenIDs(): string[] {
        return Object.keys(this._screens).sort(
            (k1, k2) =>
                this._prio(this.getScreenStatus(k2)) - this._prio(this.getScreenStatus(k1)) ||
                this.getScreenName(k1).localeCompare(this.getScreenName(k2))
        );
    }

    getActiveScreenIDs(): string[] {
        return this.getScreenIDs().filter((screenID: string) => this.getScreenStatus(screenID) === ScreenStatus.On);
    }

    getDeviceForScreen(screenID: string): string | undefined {
        return this._screens[screenID]._instanceID;
    }

    getScreenForDevice(deviceID: string): string | undefined {
        for (const key of Object.keys(this._screens)) {
            if (this._screens[key].instanceID === deviceID) {
                return this._screens[key].id;
            }
        }
        return undefined;
    }

    getGameForDevice(deviceID: string): string | undefined {
        for (const key of Object.keys(this._screens)) {
            if (this._screens[key].instanceID === deviceID) {
                return this._screens[key].gameID;
            }
        }
        return undefined;
    }

    isDemo() {
        return this.id === 'demo';
    }

    _prio(status: ScreenStatus) {
        switch (status) {
            case ScreenStatus.On:
                return 3;
            case ScreenStatus.Error:
                return 2;
            case ScreenStatus.Off:
                return 1;
            default:
                return 0;
        }
    }
}
